import {
  faImages,
  faMapMarkerAlt,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  FloatingLabel,
  Form,
  ProgressBar,
  Row
} from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ProjectImage,
  VideoProject,
  videoProjectService
} from 'services/videoService';

interface ImageUpload {
  file: File;
  preview: string;
  uploaded: boolean;
  error?: string;
}

interface ImageData {
  filename: string;
  filetype: string;
  filesize: number;
  timestamp: string;
  geo: {
    lat: number;
    long: number;
  };
  filedata: string;
}

const VideoImagesUpload = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [images, setImages] = useState<ImageUpload[]>([]);
  const [existingImages, setExistingImages] = useState<ProjectImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Load projects without video files
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        const allProjects = await videoProjectService.getAll();

        // Filter projects that don't have video files (video_filename is empty or null)
        const projectsWithoutVideo = allProjects.filter(
          project =>
            !project.video_filename || project.video_filename.trim() === ''
        );

        setProjects(projectsWithoutVideo);

        // Auto-select project if projectId is provided in URL
        const projectIdFromUrl = searchParams.get('projectId');
        if (projectIdFromUrl) {
          const projectExists = projectsWithoutVideo.some(
            project => project.id.toString() === projectIdFromUrl
          );
          if (projectExists) {
            setSelectedProjectId(projectIdFromUrl);
          }
        }
      } catch (err) {
        console.error('Error loading projects:', err);
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [searchParams]);

  // Load existing images when a project is selected
  useEffect(() => {
    const loadExistingImages = async () => {
      if (!selectedProjectId) {
        setExistingImages([]);
        setLoadingImages(false);
        return;
      }

      try {
        setLoadingImages(true);
        console.log('Loading images for project:', selectedProjectId);
        const images = await videoProjectService.listProjectImages(
          selectedProjectId
        );
        console.log('Received images from service:', images);
        console.log('Images array length:', images.length);
        setExistingImages(images);
        console.log('State updated with images');
      } catch (err) {
        console.error('Error loading existing images:', err);
        setError('Erro ao carregar imagens existentes');
      } finally {
        setLoadingImages(false);
      }
    };

    loadExistingImages();
  }, [selectedProjectId]);

  // Debug useEffect to track existingImages state changes
  useEffect(() => {
    console.log('existingImages state changed:', existingImages);
    console.log('existingImages length:', existingImages.length);
    console.log('loadingImages:', loadingImages);
  }, [existingImages, loadingImages]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newImages: ImageUpload[] = [];
    const maxFiles = 10;
    const filesToProcess = Math.min(files.length, maxFiles - images.length);

    for (let i = 0; i < filesToProcess; i++) {
      const file = files[i];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError(`Arquivo ${file.name} não é uma imagem válida`);
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(`Arquivo ${file.name} é muito grande (máximo 10MB)`);
        continue;
      }

      const preview = URL.createObjectURL(file);
      newImages.push({
        file,
        preview,
        uploaded: false
      });
    }

    setImages(prev => [...prev, ...newImages]);

    if (files.length > maxFiles) {
      setError(`Máximo de ${maxFiles} imagens permitido`);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const removeExistingImage = async (imageId: number) => {
    if (!selectedProjectId) return;

    if (window.confirm('Tem certeza que deseja remover esta imagem?')) {
      try {
        setLoadingImages(true);
        setError(null); // Clear any previous errors

        console.log('Deleting image:', imageId);
        await videoProjectService.deleteProjectImage(
          selectedProjectId,
          imageId
        );
        console.log('Image deleted successfully');

        // Reload existing images
        console.log('Reloading images list');
        const updatedImages = await videoProjectService.listProjectImages(
          selectedProjectId
        );
        setExistingImages(updatedImages);
        console.log('Images list updated');

        // Show success message briefly
        setError(null);
      } catch (err) {
        console.error('Error removing image:', err);

        // Extract detailed error message
        let errorMessage = 'Erro ao remover imagem';

        if (err && typeof err === 'object') {
          if (
            'response' in err &&
            err.response &&
            typeof err.response === 'object' &&
            'data' in err.response
          ) {
            const response = err.response as { data: unknown };
            const responseData = response.data;

            if (responseData && typeof responseData === 'object') {
              const data = responseData as Record<string, unknown>;
              if ('detail' in data && typeof data.detail === 'string') {
                errorMessage = data.detail;
              } else if (
                'message' in data &&
                typeof data.message === 'string'
              ) {
                errorMessage = data.message;
              }
            } else if (typeof responseData === 'string') {
              errorMessage = responseData;
            }
          } else if ('message' in err && typeof err.message === 'string') {
            errorMessage = err.message;
          }
        }

        setError(errorMessage);
      } finally {
        setLoadingImages(false);
      }
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix to get just the base64 data
        const base64Data = result.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const getImageLocation = (): Promise<{ lat: number; long: number }> => {
    return new Promise(resolve => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            resolve({
              lat: position.coords.latitude,
              long: position.coords.longitude
            });
          },
          () => {
            // Fallback to default coordinates if geolocation fails
            resolve({
              lat: -26.3046,
              long: -48.8488
            });
          }
        );
      } else {
        // Fallback coordinates
        resolve({
          lat: -26.3046,
          long: -48.8488
        });
      }
    });
  };

  const uploadImages = async () => {
    if (!selectedProjectId) {
      setError('Selecione um projeto');
      return;
    }

    if (images.length === 0) {
      setError('Selecione pelo menos uma imagem');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      const location = await getImageLocation();
      const imageDataArray: ImageData[] = [];

      // Convert all images to base64
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        try {
          const base64Data = await convertFileToBase64(image.file);

          const imageData: ImageData = {
            filename: image.file.name,
            filetype: image.file.type,
            filesize: image.file.size,
            timestamp: new Date().toISOString(),
            geo: location,
            filedata: `data:${image.file.type};base64,${base64Data}`
          };

          imageDataArray.push(imageData);

          // Update progress
          const progress = ((i + 1) / images.length) * 80; // 80% for processing
          setUploadProgress(progress);
        } catch (err) {
          console.error(`Error processing image ${image.file.name}:`, err);
          setImages(prev => {
            const newImages = [...prev];
            newImages[i] = {
              ...newImages[i],
              error: 'Erro ao processar imagem'
            };
            return newImages;
          });
        }
      }

      // Upload to API using video service
      await videoProjectService.uploadImages(selectedProjectId, imageDataArray);

      setUploadProgress(100);

      // Mark all images as uploaded
      setImages(prev => prev.map(img => ({ ...img, uploaded: true })));

      // Refresh existing images
      setLoadingImages(true);
      const updatedImages = await videoProjectService.listProjectImages(
        selectedProjectId
      );
      setExistingImages(updatedImages);
      setLoadingImages(false);

      // Navigate back to projects after a short delay
      setTimeout(() => {
        navigate('/app/video/projects');
      }, 1500);
    } catch (err) {
      console.error('Error uploading images:', err);

      // Extract detailed error message from API response
      let errorMessage = 'Erro ao enviar imagens';

      if (err && typeof err === 'object') {
        // Check for axios error response
        if (
          'response' in err &&
          err.response &&
          typeof err.response === 'object' &&
          'data' in err.response
        ) {
          const response = err.response as { data: unknown };
          const responseData = response.data;

          // Check for detail field in error response
          if (responseData && typeof responseData === 'object') {
            const data = responseData as Record<string, unknown>;
            if ('detail' in data && typeof data.detail === 'string') {
              errorMessage = data.detail;
            } else if ('message' in data && typeof data.message === 'string') {
              errorMessage = data.message;
            }
          } else if (typeof responseData === 'string') {
            errorMessage = responseData;
          }
        }
        // Check for direct error message
        else if ('message' in err && typeof err.message === 'string') {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    // Clean up object URLs
    images.forEach(img => URL.revokeObjectURL(img.preview));
    navigate('/app/video/projects');
  };

  return (
    <div>
      <h2 className="mb-4">
        {selectedProjectId && projects.length > 0
          ? `Editar Imagens - ${
              projects.find(p => p.id.toString() === selectedProjectId)?.name ||
              'Projeto'
            }`
          : 'Adicionar Imagens'}
      </h2>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Row>
        <Col xs={12} xl={9}>
          <Card className="mb-4">
            <Card.Body>
              <Row className="g-3">
                {/* Project Selection */}
                <Col xs={12}>
                  <FloatingLabel
                    controlId="floatingSelectProject"
                    label="Selecionar Projeto"
                  >
                    <Form.Select
                      value={selectedProjectId}
                      onChange={e => setSelectedProjectId(e.target.value)}
                      required
                      disabled={loading || uploading}
                    >
                      <option value="">
                        {loading
                          ? 'Carregando projetos...'
                          : 'Selecione um projeto sem vídeo'}
                      </option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name} - {project.description}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                  {projects.length === 0 && !loading && (
                    <Form.Text className="text-muted">
                      Nenhum projeto sem vídeo encontrado. Crie um novo projeto
                      ou gere vídeos dos projetos existentes.
                    </Form.Text>
                  )}
                </Col>

                {/* File Upload */}
                <Col xs={12}>
                  <FloatingLabel
                    controlId="floatingFileUpload"
                    label="Selecionar Imagens (máximo 10)"
                  >
                    <Form.Control
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      disabled={uploading || images.length >= 10}
                    />
                  </FloatingLabel>
                  <Form.Text className="text-muted">
                    Selecione até 10 imagens (máximo 10MB cada). Formatos
                    aceitos: JPG.
                  </Form.Text>
                </Col>

                {/* Existing Images Section - Shows after project selection */}
                {selectedProjectId && (
                  <Col xs={12}>
                    <h5>
                      Imagens do Projeto
                      {!loadingImages && `(${existingImages.length})`}
                      {existingImages.length === 0 && !loadingImages && (
                        <small className="text-muted ms-2">
                          - Nenhuma imagem encontrada
                        </small>
                      )}
                      {loadingImages && (
                        <small className="text-muted ms-2">
                          - Carregando...
                        </small>
                      )}
                    </h5>
                    {loadingImages ? (
                      <div className="text-center py-4 border rounded">
                        <div className="text-muted">
                          <div
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Carregando...
                            </span>
                          </div>
                          Carregando imagens...
                        </div>
                      </div>
                    ) : existingImages.length > 0 ? (
                      <div className="border rounded p-3">
                        {existingImages.map(image => (
                          <div
                            key={image.id}
                            className="d-flex justify-content-between align-items-center py-2 border-bottom"
                          >
                            <div className="flex-grow-1">
                              <div className="fw-medium">{image.filename}</div>
                              <div className="small text-muted">
                                {videoProjectService.getFormattedFileSize(
                                  image.size
                                )}{' '}
                                • {image.resolution} •{' '}
                                {videoProjectService.getFormattedTimestamp(
                                  image.timestamp
                                )}
                                {image.lat && image.long && (
                                  <>
                                    {' '}
                                    •{' '}
                                    <span
                                      title={`Lat: ${image.lat}, Long: ${image.long}`}
                                    >
                                      <FontAwesomeIcon icon={faMapMarkerAlt} />{' '}
                                      {image.lat.toFixed(6)},{' '}
                                      {image.long.toFixed(6)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeExistingImage(image.id)}
                              title="Remover imagem"
                              className="ms-2"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 border rounded">
                        <div className="text-muted">
                          <FontAwesomeIcon
                            icon={faImages}
                            className="fa-3x mb-3"
                          />
                          <p className="mb-0">
                            Este projeto ainda não possui imagens
                          </p>
                          <small>
                            Use o formulário acima para adicionar imagens
                          </small>
                        </div>
                      </div>
                    )}
                  </Col>
                )}

                {/* Progress Bar */}
                {uploading && (
                  <Col xs={12}>
                    <ProgressBar
                      now={uploadProgress}
                      label={`${Math.round(uploadProgress)}%`}
                      className="mb-3"
                    />
                  </Col>
                )}

                {/* Image Preview Grid */}
                {images.length > 0 && (
                  <Col xs={12}>
                    <h5>Imagens Selecionadas ({images.length}/10)</h5>
                    <Row className="g-2">
                      {images.map((image, index) => (
                        <Col xs={6} md={4} lg={3} key={index}>
                          <Card className="position-relative">
                            <Card.Img
                              variant="top"
                              src={image.preview}
                              style={{ height: '150px', objectFit: 'cover' }}
                            />
                            <Card.Body className="p-2">
                              <Card.Text className="small mb-1">
                                {image.file.name}
                              </Card.Text>
                              <Card.Text className="small text-muted mb-2">
                                {(image.file.size / 1024 / 1024).toFixed(2)} MB
                              </Card.Text>
                              {image.uploaded && (
                                <div className="text-success small">
                                  ✓ Enviado
                                </div>
                              )}
                              {image.error && (
                                <div className="text-danger small">
                                  {image.error}
                                </div>
                              )}
                            </Card.Body>
                            {!uploading && (
                              <Button
                                variant="danger"
                                size="sm"
                                className="position-absolute top-0 end-0 m-1"
                                onClick={() => removeImage(index)}
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  padding: 0
                                }}
                              >
                                ×
                              </Button>
                            )}
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Col>
                )}

                {/* Action Buttons */}
                <Col xs={12} className="gy-6">
                  <div className="d-flex justify-content-end gap-3">
                    <Button
                      variant="phoenix-primary"
                      className="px-5"
                      onClick={handleCancel}
                      disabled={uploading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      className="px-5 px-sm-15"
                      onClick={uploadImages}
                      disabled={
                        uploading ||
                        !selectedProjectId ||
                        images.length === 0 ||
                        images.some(img => img.error)
                      }
                    >
                      {uploading
                        ? 'Enviando...'
                        : `Enviar ${images.length} Imagem${
                            images.length !== 1 ? 's' : ''
                          }`}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default VideoImagesUpload;
